'use client';
import Rating from '@/entities/Rating/ui/Rating';
import { Button } from '@/shared/ui/Button';
import { Portal } from '@/shared/ui/Portal';
import { wordEnding } from '@/shared/utils/wordEnding';
import ReviewFormPopup from '@/widgets/ReviewFormPopup/ui/ReviewFormPopup';
import ReviewFormSuccessPopup from '@/widgets/ReviewFormSuccessPopup/ui/ReviewFormSuccessPopup';
import { cva } from 'class-variance-authority';
import { AnimatePresence } from 'framer-motion';
import { FC, useState } from 'react';
import {
  cvaBottomText,
  cvaButton,
  cvaHeader,
  cvaHeaderCount,
  cvaHeaderValue,
  cvaLine,
  cvaLineFill,
  cvaLines,
  cvaLineTextLeft,
  cvaLineTextRight,
  cvaLineWrap,
  cvaRoot,
  cvaStars,
} from './ProductRatingStyles';

interface Props {
  value?: number | null;
  total: number;
  details: {
    rating: string;
    total: number;
  }[];
  showBtn?: boolean;
}

const ProductRating: FC<Props> = ({ value, total, details, showBtn }) => {
  const [showForm, setShowForm] = useState(false);
  const [showFormSuccess, setShowFormSuccess] = useState(false);

  return (
    <div className={cvaRoot()}>
      <div className={cvaEmptyTitle()}>
        Отзывов о товаре <br /> пока нет
      </div>
      {value ? (
        <>
          <div className={cvaHeader()}>
            <div className={cvaHeaderValue()}>{value.toFixed(1)}</div>
            <div>
              <Rating value={value} className={cvaStars()} />
              <div className={cvaHeaderCount()}>
                {total} {wordEnding(total, ['оценка', 'оценки', 'оценок'])}
              </div>
            </div>
          </div>
          <div className={cvaLines()}>
            <div className={cvaLineWrap()}>
              <div className={cvaLineTextLeft()}>5</div>
              <div className={cvaLine()}>
                <span
                  className={cvaLineFill()}
                  style={{
                    width: (details[0].total / total) * 100 + '%',
                  }}
                ></span>
              </div>
              <div className={cvaLineTextRight()}>{details[0].total}</div>
            </div>
            <div className={cvaLineWrap()}>
              <div className={cvaLineTextLeft()}>4</div>
              <div className={cvaLine()}>
                <span
                  className={cvaLineFill()}
                  style={{
                    width: (details[1].total / total) * 100 + '%',
                  }}
                ></span>
              </div>
              <div className={cvaLineTextRight()}>{details[1].total}</div>
            </div>
            <div className={cvaLineWrap()}>
              <div className={cvaLineTextLeft()}>3</div>
              <div className={cvaLine()}>
                <span
                  className={cvaLineFill()}
                  style={{
                    width: (details[2].total / total) * 100 + '%',
                  }}
                ></span>
              </div>
              <div className={cvaLineTextRight()}>{details[2].total}</div>
            </div>
            <div className={cvaLineWrap()}>
              <div className={cvaLineTextLeft()}>2</div>
              <div className={cvaLine()}>
                <span
                  className={cvaLineFill()}
                  style={{
                    width: (details[3].total / total) * 100 + '%',
                  }}
                ></span>
              </div>
              <div className={cvaLineTextRight()}>{details[3].total}</div>
            </div>
            <div className={cvaLineWrap()}>
              <div className={cvaLineTextLeft()}>1</div>
              <div className={cvaLine()}>
                <span
                  className={cvaLineFill()}
                  style={{
                    width: (details[4].total / total) * 100 + '%',
                  }}
                ></span>
              </div>
              <div className={cvaLineTextRight()}>{details[4].total}</div>
            </div>
          </div>
        </>
      ) : null}
      {showBtn ? (
        <Button
          className={cvaButton()}
          color={'mediumGreen'}
          hoverEffect={'highlight'}
          size={'medium'}
          onClick={() => setShowForm(true)}
        >
          Оставить отзыв
        </Button>
      ) : total ? (
        <div className={cvaBottomText()}>Оформите заказ с этим товаром, чтобы оставить отзыв</div>
      ) : null}
      <AnimatePresence>
        {showForm ? (
          <Portal>
            <ReviewFormPopup
              onClose={() => setShowForm(false)}
              onSuccess={() => {
                setShowForm(false);
                setTimeout(() => {
                  setShowFormSuccess(true);
                }, 400);
              }}
            />
          </Portal>
        ) : null}
      </AnimatePresence>
      <AnimatePresence>
        {showFormSuccess ? (
          <Portal>
            <ReviewFormSuccessPopup onClose={() => setShowFormSuccess(false)} />
          </Portal>
        ) : null}
      </AnimatePresence>
    </div>
  );
};

export default ProductRating;

export const cvaEmptyTitle = cva(['ReviewsAndQaTab-cvaEmptyTitle', 'text-h3 font-secondary', 'mb-3.2']);
